import React, { useMemo, useState } from "react";
import Modal from "@/components/ui/Modal";
import { AlertCircle, CheckCircle2, X } from "lucide-react";
import { TUser } from "@/types/types";
import {
  ProfileStep,
  profileSteps,
  useProfileCompletion,
} from "@/hooks/useProfileCompletion";

const ProfileCompletionStatus = ({ data }: { data: TUser }) => {
  const [selectedStepId, setSelectedStepId] = useState<number | null>(null);
  const { totalProfileCompletion, stepCompletions, isProfileComplete } =
    useProfileCompletion(data);

  const selectedStep = useMemo(() => {
    if (!selectedStepId) return null;

    const index = profileSteps.findIndex((step) => step.id === selectedStepId);
    if (index === -1) return null;

    return {
      step: profileSteps[index] as ProfileStep,
      completion: stepCompletions[index],
    };
  }, [selectedStepId, stepCompletions]);

  return (
    <>
      <div className="rounded-xl border border-[#E4E7EC] bg-white p-5 shadow-sm">
        <div
          className={`py-[2px] px-2 rounded-lg ${
            isProfileComplete
              ? "bg-[#E7F5E9] border border-green-200"
              : "bg-[#FDECFC] border border-[#E7E7E7]"
          } inline-flex items-center gap-1`}
        >
          <div
            className={`size-1.5 rounded-full ${
              isProfileComplete ? "bg-green-600" : "bg-[#9411A9]"
            }`}
          />
          <p
            className={`font-semibold text-[11px] leading-4 ${
              isProfileComplete ? "text-green-800" : "text-[#5B0271]"
            }`}
          >
            {isProfileComplete ? "COMPLETE" : "INCOMPLETE"}
          </p>
        </div>

        <h6 className="mt-5 text-sm font-medium leading-5 text-[#101828]">
          {isProfileComplete
            ? "Profile Completed"
            : "Complete Profile to Launch Portfolio"}
        </h6>

        <div className="mt-6 flex items-center gap-4">
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-[#E9EEF5]">
            <div
              className="h-full rounded-full bg-[#1F5A96]"
              style={{ width: `${totalProfileCompletion}%` }}
            />
          </div>
          <span className="w-10 shrink-0 text-right text-sm font-medium text-[#344054]">
            {totalProfileCompletion}%
          </span>
        </div>

        <div className="mt-6 divide-y divide-[#EAECF0]">
          {profileSteps.map((step, index) => {
            const stepCompletion = stepCompletions[index];
            const isStepComplete = stepCompletion.percentage === 100;

            return (
              <button
                key={step.id}
                type="button"
                onClick={() => setSelectedStepId(step.id)}
                className="grid w-full grid-cols-[24px_1fr_42px] items-center gap-3 px-1 py-3 text-left transition-colors hover:bg-[#F9FAFB]"
              >
                {isStepComplete ? (
                  <CheckCircle2 className="size-5 shrink-0 text-green-600" />
                ) : (
                  <div className="size-5 shrink-0 rounded-full border border-[#D0D5DD]" />
                )}
                <div className="min-w-0">
                  <p className="truncate text-xs font-medium leading-[18px] text-[#101828]">
                    {step.label}
                  </p>
                  <p className="text-xs leading-[18px] text-[#667085]">
                    {stepCompletion.completedCount}/{stepCompletion.totalCount}{" "}
                    fields completed
                  </p>
                </div>
                <span className="text-right text-xs font-medium text-[#475467]">
                  {stepCompletion.percentage}%
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <Modal
        isOpen={!!selectedStep}
        setOpen={(open) => {
          if (!open) setSelectedStepId(null);
        }}
        width="420px"
        padding="0px"
      >
        <div className="bg-white">
          <div className="flex items-start justify-between gap-4 border-b border-[#EAECF0] p-5">
            <div>
              <h3 className="text-base font-semibold text-[#101828]">
                {selectedStep?.step.label}
              </h3>
              <p className="mt-1 text-sm text-[#667085]">
                {selectedStep?.completion.completedCount}/
                {selectedStep?.completion.totalCount} fields completed
              </p>
            </div>
            <button
              type="button"
              onClick={() => setSelectedStepId(null)}
              className="rounded-full p-1 text-[#667085] hover:bg-[#F2F4F7]"
              aria-label="Close"
            >
              <X className="size-5" />
            </button>
          </div>

          <div className="p-5">
            {selectedStep?.completion.missingFields.length ? (
              <div>
                <div className="mb-3 flex items-center gap-2 text-sm font-medium text-[#B42318]">
                  <AlertCircle className="size-4" />
                  Missing fields
                </div>
                <div className="space-y-2">
                  {selectedStep.completion.missingFields.map((field) => (
                    <div
                      key={field.id}
                      className="rounded-lg border border-[#FEE4E2] bg-[#FFFBFA] px-3 py-2 text-sm text-[#344054]"
                    >
                      {field.label}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div>
                <div className="mb-3 flex items-center gap-2 text-sm font-medium text-green-700">
                  <CheckCircle2 className="size-4" />
                  All fields completed
                </div>
                <div className="space-y-2">
                  {selectedStep?.completion.completedFields.map((field) => (
                    <div
                      key={field.id}
                      className="rounded-lg border border-green-100 bg-green-50 px-3 py-2 text-sm text-[#344054]"
                    >
                      {field.label}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ProfileCompletionStatus;
